import vehicleOverviewResponse from './vehicle-overview-response';
import vehicleOverviewTimelineByDay from './vehicle-overview-timeline-by-day';
import { type GetVehicleOverviewResponseVM } from '@/app/services/solution/types/get-vehicle-overview';
import { type RouteWeekDay } from '@/app/services/solution/types/get-vehicle-route';

type Temperature = '-5' | '10' | '25';

const dayIndex: Record<RouteWeekDay, number> = {
  monday: 0,
  tuesday: 1,
  wednesday: 2,
  thursday: 3,
  friday: 4,
  saturday: 5,
  sunday: 6,
};

const allowedTemperatures = new Set<Temperature>(['-5', '10', '25']);

const clampDay = (day: string): RouteWeekDay => {
  const normalized = day.trim().toLowerCase() as RouteWeekDay;
  return normalized in dayIndex ? normalized : 'monday';
};

const clampTemperature = (temperature: string): Temperature => {
  const t = temperature.trim() as Temperature;
  return allowedTemperatures.has(t) ? t : '25';
};

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

const distanceOffsetForDay = (day: RouteWeekDay): number => dayIndex[day] * 100;

const altitudeForDay = (baseAltitude: number, originalDistanceKm: number, day: RouteWeekDay): number => {
  const index = dayIndex[day];

  const baselineShift = (index - 3) * 10;

  const wave1 = Math.sin(originalDistanceKm / 7 + index * 0.9) * 12;
  const wave2 = Math.cos(originalDistanceKm / 18 + index * 0.6) * 8;

  const center = 20 + index * 15;
  const width = 22;
  const bumpAmplitude = (index % 2 === 0 ? 1 : -1) * (18 + index * 2);
  const bump = bumpAmplitude * Math.exp(-Math.pow(originalDistanceKm - center, 2) / (2 * width * width));

  const adjusted = baseAltitude + baselineShift + wave1 + wave2 + bump;

  return clamp(Math.round(adjusted), -60, 90);
};

const socDropMultiplierForTemperature: Record<Temperature, number> = {
  '25': 1,
  '10': 1.15,
  '-5': 1.35,
};

interface SocPoint {
  soc: number;
  distance: number;
  isWaypoint: boolean;
}

const isWaypointCandidate = (day: RouteWeekDay, distanceKm: number): boolean => {
  const index = dayIndex[day];
  const a = Math.sin(distanceKm / 25 + index * 0.9);
  const b = Math.cos(distanceKm / 55 + index * 0.6);
  return a + 0.7 * b > 0.9;
};

const buildWaypointMask = (day: RouteWeekDay, points: SocPoint[]): boolean[] => {
  const n = points.length;
  if (n === 0) return [];

  const mask = points.map((p, index) => isWaypointCandidate(day, p.distance) && index !== 0 && index !== n - 1);

  mask[0] = true;
  if (n > 1) mask[n - 1] = true;

  if (n > 2) {
    const hasMiddle = mask.slice(1, n - 1).some(Boolean);
    if (!hasMiddle) {
      const middleStart = Math.floor((n - 1) / 2);
      const offset = (dayIndex[day] % 3) - 1;
      const chosen = clamp(middleStart + offset, 1, n - 2);
      mask[chosen] = true;
    }
  }

  return mask;
};

const adjustSocForTemperature = (points: SocPoint[], temperature: Temperature, day: RouteWeekDay): SocPoint[] => {
  if (points.length === 0) return points;

  const multiplier = socDropMultiplierForTemperature[temperature];
  const startSoc = points[0].soc;

  const waypointMask = buildWaypointMask(day, points);

  const out: SocPoint[] = [];

  const pushOrFixPreChargeAtSameDistance = (distance: number, preChargeSoc: number): void => {
    const last = out.at(-1);
    if (last?.distance === distance) {
      out[out.length - 1] = {
        ...last,
        isWaypoint: true,
        soc: preChargeSoc,
      };
    } else {
      out.push({
        distance: distance,
        isWaypoint: true,
        soc: preChargeSoc,
      });
    }
  };

  for (let index = 0; index < points.length; index++) {
    const p = points[index];
    const isWaypoint = waypointMask[index];

    if (index === 0) {
      out.push({
        ...p,
        isWaypoint: true,
        soc: clamp(Math.round(p.soc), 0, 100),
      });
      continue;
    }

    const baseDrop = startSoc - p.soc;

    const temporaryAdjusted = clamp(Math.round(startSoc - baseDrop * multiplier), 0, 100);

    const previousSoc = out.at(-1)?.soc ?? clamp(Math.round(points[index - 1].soc), 0, 100);

    const arrivalSoc = clamp(Math.min(temporaryAdjusted, previousSoc), 0, 100);

    const postChargeSoc = isWaypoint ? temporaryAdjusted : arrivalSoc;

    if (isWaypoint && postChargeSoc > arrivalSoc) {
      pushOrFixPreChargeAtSameDistance(p.distance, arrivalSoc);

      out.push({
        ...p,
        isWaypoint: true,
        soc: postChargeSoc,
      });
    } else {
      out.push({
        ...p,
        isWaypoint: isWaypoint,
        soc: postChargeSoc,
      });
    }
  }

  if (out.length > 0) {
    const last = out.at(-1);
    if (last) {
      out[out.length - 1] = { ...last, isWaypoint: true };
    }
  }

  return out;
};

const getVehicleOverview = ({
  day,
  temperature,
}: {
  day: string;
  temperature: string;
}): Promise<GetVehicleOverviewResponseVM> => {
  const safeDay = clampDay(day);
  const safeTemperature = clampTemperature(temperature);

  const distanceOffset = distanceOffsetForDay(safeDay);

  const full = vehicleOverviewResponse.batteryLevel.fullBatterySoc;
  const lower = vehicleOverviewResponse.batteryLevel.lowerBatterySoc;

  return Promise.resolve({
    ...vehicleOverviewResponse,
    timeline: vehicleOverviewTimelineByDay[safeDay],
    routeAltitude: vehicleOverviewResponse.routeAltitude.map((routeAltitude) => ({
      distance: routeAltitude.distance + distanceOffset,
      altitude: altitudeForDay(routeAltitude.altitude, routeAltitude.distance, safeDay),
    })),
    batteryLevel: {
      fullBatterySoc: adjustSocForTemperature(full, safeTemperature, safeDay).map((item) => ({
        ...item,
        distance: item.distance + distanceOffset,
      })),
      lowerBatterySoc: adjustSocForTemperature(lower, safeTemperature, safeDay).map((item) => ({
        ...item,
        distance: item.distance + distanceOffset,
      })),
    },
  });
};

export default getVehicleOverview;
